import React, { useMemo, useState, useEffect, useContext } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import './style.css';


const SkusTable = ({ project, preEnhanced = false, selectedVendorId = null, inGettingItDone = false }) => {

    const { deviceType } = useContext(WindowSizeContext);

    // Get the portalData from the AddressSearchContext
    const { portalData } = useContext(AddressSearchContext);

    // Create the state variables
    const [skusTruncated, setSkusTruncated] = useState([]);
    const [skusFull, setSkusFull] = useState([]);
    const [seeAllSkus, setSeeAllSkus] = useState(false);

    const [localData, setLocalData] = useState({
        skus: [],
        companies: [],
        icons: {}
    });

    // Load data from localStorage once on mount
    useEffect(() => {
        const skus = portalData.skus || [];
        const companies = portalData.companies || [];
        const icons = portalData.icons || {};
        setLocalData({ skus, companies, icons });
    }, []);

    // Enrich the project_skus with additional information and sort by cost
    const enriched_skus = useMemo(() => {
        const project_skus = project.project_skus || [];

        let skusToUse = project_skus;

        if (selectedVendorId) {
            skusToUse = project_skus.filter(sku => sku.sold_by === selectedVendorId);
        }

        return skusToUse
            .map(sku => {

                let product = null;
                if (preEnhanced) {
                    product = sku;
                    product.cost = product.totalCost;
                } else {
                    product = localData.skus.find(
                        local_sku => local_sku.product_id === sku.id
                    );
                }


                if (!product) return sku;

                const sold_by_company = localData.companies.find(
                    company => company.id === product.sold_by
                );

                const made_by_company = localData.companies.find(
                    company => company.id === product.made_by
                );

                return {
                    ...sku,
                    product_name: product.product_name,
                    unit_cost: product.unit_cost,
                    url: product.url,
                    sold_by_name: sold_by_company?.name,
                    sold_by_company_type: sold_by_company?.company_type,
                    sold_by_url: sold_by_company?.company_url,
                    sold_by_icon: CreateCloudfrontUrl(localData.icons[sold_by_company?.icon]),
                    made_by_name: made_by_company?.name,
                    made_by_company_type: made_by_company?.company_type,
                    made_by_url: made_by_company?.company_url,
                    made_by_icon: CreateCloudfrontUrl(localData.icons[made_by_company?.icon])
                };
            })
            .sort((a, b) => a.cost - b.cost);
    }, [project.project_skus, localData]);

    const truncateAt = 5;
    const skusRemainingCount = enriched_skus.length - truncateAt;

    const skusFirstShowing = enriched_skus.slice(0, truncateAt);

    // console.log('Enriched SKUs:', enriched_skus);

    const getting_it_done_intro = "Let's point you towards the products you're going to need. Below are various options that all fit this project, and remember, if you add this project to your Taking Action hub we may be able to get you additional discounts!";

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.accessor('sold_by_icon', {
            header: 'Where to Find',
            cell: info => (
                <div className="vendor-icon-container">
                    <img
                        src={info.getValue()}
                        alt="Vendor logo"
                        className="vendor-icon"
                    />
                </div>
            ),
        }),
        columnHelper.accessor('product_name', {
            header: 'Product Name',
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('made_by_name', {
            header: 'Made By',
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('cost', {
            header: 'Est Total Cost',
            cell: info => `${Math.round(info.getValue()).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
        }),
        columnHelper.accessor('url', {
            header: 'Go',
            cell: info => (
                <button
                    className="product-link-button"
                    onClick={() => window.open(info.getValue(), '_blank')}
                >
                    View
                </button>
            ),
        }),
    ], []);

    let columnsToUse = columns;

    // If inGettingItDone is true then remove the first column
    if (inGettingItDone) {
        columnsToUse = columnsToUse.slice(1);
    }

    const table = useReactTable({
        data: seeAllSkus ? enriched_skus : skusFirstShowing,
        columns: columnsToUse,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div className={`products-table-container ${deviceType} ${preEnhanced ? 'getting-it-done-table' : ''}`}>
            <table className={`products-table ${deviceType} ${preEnhanced ? 'getting-it-done-table' : ''}`}>
                <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th key={header.id}>
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {(!seeAllSkus && skusRemainingCount > 0) &&
                <button className={`see-all-skus-button ${deviceType}`} onClick={() => setSeeAllSkus(true)}>See {skusRemainingCount} More</button>
            }
        </div>
    );
}

export default SkusTable;